import { useState } from "react";
import { Upload } from "lucide-react";
import { AppContainer } from "@/components/layout/app-container";
import { Button } from "@/components/ui/button";
import {
  DocumentosStatsSection,
  DocumentosTable,
  DocumentViewModal,
  TimeRangeFilter,
  DocumentTypeFilter,
  StageFilter,
  DeleteDocumentDialog,
  UploadDocumentDialog,
} from "@/components/documentos";
import { IDocumento, IDocumentFilters, IDocumentoEnhanced } from "@/types/documento";
import { useDocumentos, useDeleteDocumento, useDocumentosStats } from "@/services/queries/useDocumentos";

export default function DocumentosPage() {
  const [selectedDocument, setSelectedDocument] = useState<IDocumentoEnhanced | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<IDocumento | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  // API hooks
  const { data: documents, isLoading } = useDocumentos();
  const { data: stats, isLoading: isLoadingStats } = useDocumentosStats();
  const deleteMutation = useDeleteDocumento();

  // Filter state management
  const [filters, setFilters] = useState<IDocumentFilters>({
    searchTerm: "",
    timeRange: "todos",
    documentType: [],
    stage: [],
  });

  // Filter change handlers
  const handleTimeRangeChange = (timeRange: IDocumentFilters["timeRange"]) => {
    setFilters((prev) => ({ ...prev, timeRange }));
  };

  const handleDocumentTypeChange = (documentType: IDocumentFilters["documentType"]) => {
    setFilters((prev) => ({ ...prev, documentType }));
  };

  const handleStageChange = (stage: IDocumentFilters["stage"]) => {
    setFilters((prev) => ({ ...prev, stage }));
  };

  const handleViewDocument = (doc: IDocumento) => {
    setSelectedDocument(doc);
    setModalOpen(true);
  };

  const handleEditDocument = (doc: IDocumento) => {
    // TODO: Implement edit functionality
    console.log("Edit document:", doc);
  };

  const handleDeleteDocument = (doc: IDocumento) => {
    setDocumentToDelete(doc);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (documentToDelete) {
      deleteMutation.mutate(documentToDelete.id);
    }
    setDocumentToDelete(null);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDocumentToDelete(null);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedDocument(null);
  };

  if (isLoading) {
    return (
      <AppContainer>
        <div className="flex items-center justify-center min-h-[400px]">
          <p className="text-[#888888] text-[16px] font-['Plus_Jakarta_Sans']">
            Carregando documentos...
          </p>
        </div>
      </AppContainer>
    );
  }

  return (
    <AppContainer>
      {/* Header with purple divider and filters */}
      <div className="mb-[30px]">
        {/* Purple divider bar */}
        <div className="h-[4px] w-full bg-[#7846FF] rounded-full mb-[20px]" />

        {/* Title and time filter */}
        <div className="flex items-center justify-between mb-[20px]">
          <h1 className="text-[32px] font-bold text-white font-['Plus_Jakarta_Sans']">
            Documentos
          </h1>

          <div className="flex items-center gap-[16px]">
            <TimeRangeFilter
              value={filters.timeRange}
              onChange={handleTimeRangeChange}
            />
            <Button
              variant="default"
              onClick={() => setUploadDialogOpen(true)}
              className="h-[50px] px-[20px] flex items-center gap-[10px]"
            >
              <Upload className="size-[20px]" />
              Enviar Documento
            </Button>
          </div>
        </div>

        {/* Document type and stage filters */}
        <div className="flex items-center gap-[16px]">
          <DocumentTypeFilter
            value={filters.documentType}
            onChange={handleDocumentTypeChange}
          />
          <StageFilter
            value={filters.stage}
            onChange={handleStageChange}
          />
        </div>
      </div>

      {/* Stats Section */}
      <div className="mb-[30px]">
        <DocumentosStatsSection stats={stats ?? []} isLoading={isLoadingStats} />
      </div>

      {/* Documents Table */}
      <DocumentosTable
        documents={documents ?? []}
        filters={filters}
        onViewDocument={handleViewDocument}
        onEditDocument={handleEditDocument}
        onDeleteDocument={handleDeleteDocument}
      />

      {/* Document Viewer Modal */}
      <DocumentViewModal
        document={selectedDocument}
        open={modalOpen}
        onClose={handleCloseModal}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteDocumentDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
        documentName={documentToDelete?.fileName || ""}
      />

      {/* Upload Document Dialog */}
      <UploadDocumentDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onSuccess={() => setUploadDialogOpen(false)}
      />
    </AppContainer>
  );
}
